/**
 * Device Fingerprinting Service
 * 
 * Provides consistent device identification across the application.
 * Used by analytics tracking, split testing, and other services that need
 * reliable device identification for anonymous users.
 */

/**
 * Generate a browser fingerprint for device identification
 * 
 * Creates a stable identifier based on browser and device characteristics.
 * This fingerprint remains consistent across sessions for the same device/browser.
 * 
 * @returns {string} - Unique device fingerprint
 */
export const generateDeviceFingerprint = () => {
  if (typeof window === 'undefined') return 'server-side';
  
  try {
    const components = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      navigator.hardwareConcurrency,
      navigator.deviceMemory || 'unknown',
      window.devicePixelRatio,
    ];
    
    // Use a more robust encoding method that works in all browsers
    const fingerprint = components.join('|');
    
    // Simple hash function that works everywhere
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Convert to a 16-character string
    return Math.abs(hash).toString(16).padStart(8, '0') + 
           Math.abs(hash * 31).toString(16).padStart(8, '0');
  } catch (error) {
    console.warn('Device fingerprint generation failed, using fallback:', error);
    // Fallback to a simple random-like ID based on current time
    return 'fallback_' + Date.now().toString(36);
  }
};

/**
 * Get a consistent user identifier for anonymous users
 * 
 * This is the primary function other services should use when they need
 * a reliable identifier for tracking or assignment purposes.
 * 
 * @returns {string} - Consistent user identifier
 */
export const getUserIdentifier = () => {
  return generateDeviceFingerprint();
}; 